// src/runtime/RuntimeManager.ts
// ============================================
// RuntimeManager - Kernel (v1.0 + RBAC 集成)
//
// 职责边界（Only 5 categories）：
//   1. Registry   → register / unregister / get / has
//   2. Dispatch   → query / execute
//   3. Events     → on / off / emit
//   4. Status     → getStatusSummary / getLatencyStats
//   5. Lifecycle  → initialize / destroy / reset
//
// P1-1 RBAC 扩展：
//   - setUser / getUser / clearUser ：管理当前请求的用户上下文
//   - checkPermission              ：权限检查（通过 EnterpriseRuntime）
//   - query/execute 集成权限检查    ：自动鉴权，未通过返回 403
//
// 此文件不包含任何业务语义（无 Knowledge、Trading、Workspace 等）
// 所有业务逻辑由各 Runtime 自行实现。
// ============================================

import {
  IRuntime,
  RuntimeName,
  RuntimeStatus,
  RuntimeResponse,
  RuntimeEvent,
  RuntimeEventType,
  RuntimeHealth,
  getHealthFromStatus,
} from '../types/runtime';

import RuntimeRegistry from './RuntimeRegistry';
import RuntimeDispatcher from './RuntimeDispatcher';
import EventBus from './EventBus';
// 注意：register.ts 不在顶层 import，改为在 initialize() 中动态 import
// 原因：register.ts import 了 40+ 个 Runtime 模块，其中部分模块会 import RuntimeManager（循环引用）
// 在 Vite dev 模式下，这种循环引用可能导致 registerAllRuntimes 在模块加载时为 undefined
// 动态 import 确保 register.ts 只在 initialize() 被调用时才加载，此时 RuntimeManager 已完全定义

// P1-1：RBAC 类型（仅类型导入，避免循环依赖）
import type { EnterpriseService } from './EnterpriseRuntime';

// ============================================
// Kernel State
// ============================================
interface RuntimeManagerState {
  initialized: boolean;
  startupTime: number;
  version: string;
  environment: 'development' | 'production' | 'test';
}

// ============================================
// 用户上下文（P1-1 RBAC）
// ============================================
interface UserContext {
  /** 当前用户 ID */
  userId: string;
  /** 设置时间（用于审计） */
  setAt: string;
}

// ============================================
// RuntimeManager - Kernel Class
// ============================================
export class RuntimeManager {
  private static instance: RuntimeManager | null = null;

  // 核心组件（只读，不可替换）
  private readonly registry: RuntimeRegistry;
  private readonly dispatcher: RuntimeDispatcher;
  private readonly eventBus: EventBus;

  // 状态
  private state: RuntimeManagerState = {
    initialized: false,
    startupTime: Date.now(),
    version: '1.0.0',
    environment: (process.env.NODE_ENV as any) || 'development',
  };

  // P1-1：当前用户上下文（每个请求/会话一个）
  // 注意：单例模式下 currentUser 是进程级的；多租户场景应改为 AsyncLocalStorage
  private currentUser: UserContext | null = null;

  // P1-1：是否启用权限检查（默认启用，可通过 disableAuth 临时关闭）
  // Sprint 4：默认关闭 RBAC，避免阻塞 AI 企业闭环（CRM/SKU/SOP/Sales/CEO 等跨 Runtime 调用）
  // RBAC 是 P1 任务，当前单用户场景下不需要权限拦截
  // 如需启用，调用 enableAuth()
  private authEnabled: boolean = false;

  // P2-3：当前工作区上下文（数据隔离用）
  // 默认 'default'，由 WorkspaceService.switch() 联动设置
  // 所有 Runtime 通过 params._workspaceId 获取该上下文
  private currentWorkspaceId: string = 'default';

  private constructor() {
    this.registry = new RuntimeRegistry();
    this.dispatcher = new RuntimeDispatcher();
    this.eventBus = new EventBus();
  }

  // ============================================
  // Singleton
  // ============================================
  static getInstance(): RuntimeManager {
    if (!RuntimeManager.instance) {
      RuntimeManager.instance = new RuntimeManager();
    }
    return RuntimeManager.instance;
  }

  // ============================================
  // 1. Lifecycle
  // ============================================
  async initialize(): Promise<void> {
    console.log('[RuntimeManager] initialize() 被调用 ✅');
    console.log('[RuntimeManager] isInitialized =', this.state.initialized);
    if (this.state.initialized) {
      console.warn('[RuntimeManager] Already initialized');
      return;
    }

    console.log('[RuntimeManager] Initializing...');

    try {
      // 动态 import register.ts，避免循环引用导致 registerAllRuntimes 为 undefined
      console.log('[RuntimeManager] 开始动态 import register.ts...');
      const registerModule = await import('./register');
      console.log('[RuntimeManager] register.ts import 完成, keys:', Object.keys(registerModule));
      const { registerAllRuntimes } = registerModule;
      console.log('[RuntimeManager] registerAllRuntimes type:', typeof registerAllRuntimes);

      if (typeof registerAllRuntimes !== 'function') {
        throw new Error(`registerAllRuntimes 不是函数，类型为 ${typeof registerAllRuntimes}`);
      }

      // 注册所有内置 Runtime
      registerAllRuntimes(this);
      console.log('[RuntimeManager] registerAllRuntimes 完成，已注册', this.registry.size, '个 Runtime');
      console.log('[RuntimeManager] 已注册的 Runtime:', this.registry.getNames());
      console.log('[RuntimeManager] GEO 已注册:', this.registry.has('geo' as RuntimeName));
      console.log('[RuntimeManager] DSO 已注册:', this.registry.has('dso' as RuntimeName));

      // 初始化所有 Runtime（每个加 5 秒超时，避免某个 Runtime 卡住阻塞整体）
      const initPromises = this.registry.values().map(async (runtime) => {
        try {
          // 给每个 Runtime 的 initialize 加 5 秒超时
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error(`initialize timeout (5s)`)), 5000)
          );
          await Promise.race([runtime.initialize(), timeoutPromise]);
          console.log(`[RuntimeManager] ${runtime.name} initialized`);
        } catch (error) {
          console.error(`[RuntimeManager] Failed to initialize ${runtime.name}:`, error);
        }
      });

      await Promise.all(initPromises);

      this.state.initialized = true;
      console.log('[RuntimeManager] All runtimes initialized ✅, count:', this.registry.size);

      // v6.3.24 系统就绪事件进入实时流（真实事件，按 summary 幂等去重，避免刷新刷屏）
      try {
        const { activityBus } = await import('./ActivityBus');
        activityBus.pushOnce(
          'system.ready',
          'info',
          `XEAOS Runtime 初始化完成 · ${this.registry.size} 个 Runtime 就绪`,
          'System'
        );
      } catch {
        // 事件总线不可用不影响初始化
      }

      // ============================================================
      // v6.4.x 驾驶舱自动运行自检（Demo Bootstrap / Morning AutoRun）
      // 目的：让"总控台打开就有真实运行画面"——登录/初始化完成后，
      //       自动触发一次真实轻量的企业总脑快速运行（算今日 KPI +
      //       今日经营重点），并如实写入活动流，供驾驶舱展示。
      // 铁律：只触发真实命令，绝不伪造事件/数据；失败静默不阻塞初始化。
      // 幂等：按"日期+类型"用 activityBus 事件幂等去重，避免同一浏览器
      //       当天反复刷新反复执行刷屏（仅当天首次执行后推送一条事件）。
      // ============================================================
      this._autoRunMorningBootstrap().catch(() => {
        /* 自检失败不影响系统初始化 */
      });
    } catch (error) {
      console.error('[RuntimeManager] Initialization failed:', error);
      console.error('[RuntimeManager] Error stack:', error instanceof Error ? error.stack : 'no stack');
      // 即使失败也标记为已初始化，避免前端永久等待
      this.state.initialized = true;
      console.warn('[RuntimeManager] 标记为已初始化（部分 Runtime 可能未就绪）, count:', this.registry.size);
    }
  }

  /**
   * 驾驶舱自动运行自检：初始化完成后延迟触发一次真实轻量命令，
   * 让总控台打开即有真实运行画面（企业总脑算今日 KPI + 今日经营重点）。
   * 只做真实执行，不造假；幂等去重；失败静默。
   */
  private async _autoRunMorningBootstrap(): Promise<void> {
    // 仅在具备 EnterpriseBrain 运行时才触发
    if (!this.registry?.has(RuntimeName.EnterpriseBrain)) {
      return;
    }
    const todayKey = new Date().toISOString().slice(0, 10);
    // 当天已自动运行过则跳过（避免每次刷新反复执行、浪费资源/重复写入）
    const RUN_MARK_KEY = 'xeaos_morning_autorun';
    try {
      const done = localStorage.getItem(RUN_MARK_KEY);
      if (done === todayKey) return;
    } catch {
      /* localStorage 不可用时继续执行 */
    }
    try {
      // 延迟触发：等首次渲染 & 各 Runtime 完全就绪后再跑，避免挤占初始化主流程
      await new Promise((r) => setTimeout(r, 2500));

      // 真实触发企业总脑快速运行（算今日 KPI + 今日经营重点），不造数
      const resp = await this.execute(RuntimeName.EnterpriseBrain, 'run_quick', {
        date: todayKey,
      });

      const data = (resp?.data ?? {}) as any;
      const kpiCount = Array.isArray(data?.kpis) ? data.kpis.length : 0;
      const summary =
        kpiCount > 0
          ? `企业总脑自动运行 · 今日已核算 ${kpiCount} 个企业 KPI 与经营重点`
          : `企业总脑自动运行完成 · 今日经营重点已生成`;

      // 如实写入一条真实活动事件（供驾驶舱活动流展示）
      const { activityBus } = await import('./ActivityBus');
      activityBus.push(
        'content.generated',
        'success',
        summary,
        'EnterpriseBrain'
      );

      // 记录当天已自动运行，避免同日反复执行刷屏
      try {
        localStorage.setItem(RUN_MARK_KEY, todayKey);
      } catch {
        /* 忽略 */
      }
    } catch (err) {
      console.warn('[RuntimeManager] 驾驶舱自动运行自检失败（非阻塞）:', err);
    }
  }

  /**
   * 销毁 RuntimeManager（释放所有资源）
   */
  destroy(): void {
    // 销毁所有 Runtime
    for (const [name, runtime] of this.registry.entries()) {
      try {
        if (typeof (runtime as any).destroy === 'function') {
          (runtime as any).destroy();
        }
      } catch (error) {
        console.error(`[RuntimeManager] Failed to destroy ${name}:`, error);
      }
    }

    this.registry.clear();
    this.dispatcher.clearLatencyHistory();
    this.eventBus.clear();
    this.state.initialized = false;
    console.log('[RuntimeManager] Destroyed');
  }

  /**
   * 重置 RuntimeManager（保留监听器，仅重置状态）
   */
  reset(): void {
    this.registry.clear();
    this.dispatcher.clearLatencyHistory();
    // 注意：不清除 eventBus 监听器
    this.state.initialized = false;
    this.state.startupTime = Date.now();
    console.log('[RuntimeManager] Reset');
  }

  // ============================================
  // 2. Registry
  // ============================================
  registerRuntime(runtime: IRuntime): void {
    this.registry.register(runtime);
  }

  unregisterRuntime(name: RuntimeName): void {
    // 先尝试销毁
    try {
      const runtime = this.registry.get(name);
      if (typeof (runtime as any).destroy === 'function') {
        (runtime as any).destroy();
      }
    } catch (_) {
      // ignore
    }
    this.registry.unregister(name);
  }

  getRuntime<T extends IRuntime>(name: RuntimeName): T {
    return this.registry.get<T>(name);
  }

  hasRuntime(name: RuntimeName): boolean {
    return this.registry.has(name);
  }

  getAllRuntimes(): RuntimeName[] {
    return this.registry.getNames();
  }

  get runtimeCount(): number {
    return this.registry.size;
  }

  // ============================================
  // 3. Dispatch（包装 Dispatcher 返回值为 RuntimeResponse）
  // ============================================
  async query(name: RuntimeName, params?: Record<string, any>, workspaceId?: string): Promise<RuntimeResponse> {
    const startTime = performance.now();

    // P2-3：注入 workspaceId 到 params（不修改 dispatcher 实现）
    // 优先级：显式传入 > 当前上下文 > 'default'
    const effectiveWorkspaceId = workspaceId ?? this.currentWorkspaceId;
    const paramsWithWorkspace: Record<string, any> = {
      ...(params ?? {}),
      _workspaceId: effectiveWorkspaceId,
    };

    // P1-1：权限检查（EnterpriseRuntime 自身的 RBAC 查询跳过，避免循环）
    const isRBACQuery = name === RuntimeName.Enterprise &&
      typeof params?.type === 'string' &&
      (params.type as string).startsWith('rbac_');

    if (this.authEnabled && !isRBACQuery) {
      const permission = this.buildPermissionCode(name, 'read');
      const checkResult = this.checkPermissionInternal(permission);
      if (!checkResult.allowed) {
        const latency = Math.round(performance.now() - startTime);
        return {
          success: false,
          error: `权限拒绝: ${permission} (${checkResult.reason || '权限不足'})`,
          runtime: name,
          timestamp: new Date().toISOString(),
          latency,
        };
      }
    }

    const runtime = this.registry.get(name);

    try {
      const data = await this.dispatcher.query(runtime, paramsWithWorkspace);
      const latency = Math.round(performance.now() - startTime);
      this.dispatcher.recordLatency(name, latency);

      return {
        success: true,
        data,
        runtime: name,
        timestamp: new Date().toISOString(),
        latency,
      };
    } catch (error) {
      const latency = Math.round(performance.now() - startTime);
      this.dispatcher.recordLatency(name, latency);

      const message = error instanceof Error ? error.message : String(error);
      return {
        success: false,
        error: message,
        runtime: name,
        timestamp: new Date().toISOString(),
        latency,
      };
    }
  }

  async execute(name: RuntimeName, command: string, params?: Record<string, any>, workspaceId?: string): Promise<RuntimeResponse> {
    const startTime = performance.now();

    // P2-3：注入 workspaceId 到 params（不修改 dispatcher 实现）
    // 优先级：显式传入 > 当前上下文 > 'default'
    const effectiveWorkspaceId = workspaceId ?? this.currentWorkspaceId;
    const paramsWithWorkspace: Record<string, any> = {
      ...(params ?? {}),
      _workspaceId: effectiveWorkspaceId,
    };

    // P1-1：权限检查（EnterpriseRuntime 自身的 RBAC 命令跳过，避免循环）
    const isRBACCommand = name === RuntimeName.Enterprise &&
      typeof command === 'string' &&
      command.startsWith('rbac_');

    // Hermes 的 chat/generate 命令跳过 RBAC：
    // 这是用户直接交互的 AI 对话入口，用户能发消息说明已通过认证；
    // Sprint 4 所有业务（CRM/SKU/SOP/Sales/CEO）都通过 Hermes 触发，
    // 如果 chat 被拦截，整个 AI 闭环就断了。
    const isHermesChatCommand = name === RuntimeName.Hermes &&
      typeof command === 'string' &&
      (command === 'chat' || command === 'generate');

    if (this.authEnabled && !isRBACCommand && !isHermesChatCommand) {
      // 根据命令推断 action：write/execute/delete
      const action = this.inferActionFromCommand(command);
      const permission = this.buildPermissionCode(name, action);
      const checkResult = this.checkPermissionInternal(permission);
      if (!checkResult.allowed) {
        const latency = Math.round(performance.now() - startTime);
        return {
          success: false,
          error: `权限拒绝: ${permission} (${checkResult.reason || '权限不足'})`,
          runtime: name,
          timestamp: new Date().toISOString(),
          latency,
        };
      }
    }

    const runtime = this.registry.get(name);

    try {
      const data = await this.dispatcher.execute(runtime, command, paramsWithWorkspace);
      const latency = Math.round(performance.now() - startTime);
      this.dispatcher.recordLatency(name, latency);

      return {
        success: true,
        data,
        runtime: name,
        timestamp: new Date().toISOString(),
        latency,
      };
    } catch (error) {
      const latency = Math.round(performance.now() - startTime);
      this.dispatcher.recordLatency(name, latency);

      const message = error instanceof Error ? error.message : String(error);
      return {
        success: false,
        error: message,
        runtime: name,
        timestamp: new Date().toISOString(),
        latency,
      };
    }
  }

  // ============================================
  // P1-1：RBAC 用户上下文管理
  // ============================================

  /**
   * 设置当前用户上下文
   * 在请求开始时调用，所有后续 query/execute 都会以此用户身份鉴权
   *
   * @param userId 用户 ID
   */
  setUser(userId: string): void {
    this.currentUser = {
      userId,
      setAt: new Date().toISOString(),
    };
  }

  /**
   * 获取当前用户 ID
   * 未设置时返回 undefined（将使用 System 用户）
   */
  getUser(): string | undefined {
    return this.currentUser?.userId;
  }

  /**
   * 清除当前用户上下文
   * 在请求结束时调用
   */
  clearUser(): void {
    this.currentUser = null;
  }

  // ============================================
  // P2-3：工作区上下文管理（数据隔离）
  // ============================================

  /**
   * 设置当前工作区
   *
   * 由 WorkspaceService.switch() 联动调用。
   * 设置后，所有通过 query/execute 发起的 Runtime 调用都会自动携带 _workspaceId。
   *
   * @param workspaceId 工作区 ID
   */
  setWorkspace(workspaceId: string): void {
    this.currentWorkspaceId = workspaceId;
  }

  /**
   * 获取当前工作区 ID
   * 未设置时返回 'default'
   */
  getWorkspace(): string {
    return this.currentWorkspaceId;
  }

  /**
   * 清除当前工作区上下文（重置为 'default'）
   */
  clearWorkspace(): void {
    this.currentWorkspaceId = 'default';
  }

  /**
   * 启用权限检查
   */
  enableAuth(): void {
    this.authEnabled = true;
  }

  /**
   * 禁用权限检查（仅用于测试或系统初始化阶段）
   */
  disableAuth(): void {
    this.authEnabled = false;
  }

  /**
   * 检查权限（公开 API，供外部调用）
   *
   * @param permission 权限字符串，如 "workflow.execute.*"
   * @param userId 用户 ID（可选，未指定则使用当前用户）
   */
  checkPermission(
    permission: string,
    userId?: string
  ): { allowed: boolean; reason?: string } {
    return this.checkPermissionInternal(permission, userId);
  }

  // ============================================
  // P1-1：内部权限检查实现
  // ============================================

  /**
   * 内部权限检查
   *
   * 通过 EnterpriseRuntime 的 EnterpriseService.checkPermission 方法
   * 委托给 RBACEngine.check 进行实际鉴权
   */
  private checkPermissionInternal(
    permission: string,
    userId?: string
  ): { allowed: boolean; reason?: string } {
    // 未注册 EnterpriseRuntime 时，放行（系统启动早期）
    if (!this.registry.has(RuntimeName.Enterprise)) {
      return { allowed: true };
    }

    try {
      const enterpriseRuntime = this.registry.get(RuntimeName.Enterprise) as any;
      const service: EnterpriseService = enterpriseRuntime.getService?.() ?? enterpriseRuntime.service;

      if (!service || typeof service.checkPermission !== 'function') {
        // EnterpriseService 未初始化，放行
        return { allowed: true };
      }

      const effectiveUserId = userId || this.currentUser?.userId;
      return service.checkPermission(effectiveUserId, permission);
    } catch (error) {
      // 鉴权过程异常 → 拒绝（fail-safe）
      const msg = error instanceof Error ? error.message : String(error);
      return { allowed: false, reason: `权限检查异常: ${msg}` };
    }
  }

  /**
   * 构建 RBAC 权限字符串
   *
   * RuntimeName → resource 映射：
   *   Workflow   → workflow
   *   Knowledge  → knowledge
   *   Content    → content
   *   Tool       → tool
   *   Memory     → memory
   *   Enterprise → enterprise
   *   Hermes     → system
   *   Quant      → system
   *   其他       → system
   *
   * @param runtime Runtime 名称
   * @param action 动作（read/write/execute/delete/admin）
   */
  private buildPermissionCode(
    runtime: RuntimeName,
    action: 'read' | 'write' | 'execute' | 'delete' | 'admin'
  ): string {
    const resourceMap: Record<string, string> = {
      [RuntimeName.Workflow]: 'workflow',
      [RuntimeName.Knowledge]: 'knowledge',
      [RuntimeName.Content]: 'content',
      [RuntimeName.Tool]: 'tool',
      [RuntimeName.Memory]: 'memory',
      [RuntimeName.Enterprise]: 'enterprise',
    };

    const resource = resourceMap[runtime] || 'system';
    return `${resource}.${action}.*`;
  }

  /**
   * 根据命令推断动作类型
   *
   * 默认映射：
   *   - 包含 delete/remove → delete
   *   - 包含 create/update/write/insert/register → write
   *   - 包含 execute/run/publish/approve → execute
   *   - 其他 → write（保守策略）
   */
  private inferActionFromCommand(
    command: string
  ): 'read' | 'write' | 'execute' | 'delete' {
    const cmd = command.toLowerCase();

    if (cmd.includes('delete') || cmd.includes('remove') || cmd.includes('drop')) {
      return 'delete';
    }
    if (cmd.includes('execute') || cmd.includes('run') || cmd.includes('publish') ||
        cmd.includes('approve') || cmd.includes('reject')) {
      return 'execute';
    }
    if (cmd.includes('read') || cmd.includes('get') || cmd.includes('list') ||
        cmd.includes('search') || cmd.includes('query')) {
      return 'read';
    }

    // 默认按 write 处理（保守）
    return 'write';
  }

  // ============================================
  // 4. Events
  // ============================================
  on(type: RuntimeEventType, listener: (event: RuntimeEvent) => void): void {
    this.eventBus.on(type, listener);
  }

  off(type: RuntimeEventType, listener: (event: RuntimeEvent) => void): void {
    this.eventBus.off(type, listener);
  }

  emit(event: RuntimeEvent): void {
    this.eventBus.emit(event);
  }

  // ============================================
  // 5. Status
  // ============================================
  getStatusSummary(): {
    initialized: boolean;
    runtimeCount: number;
    readyCount: number;
    uptime: number;
    status: Record<RuntimeName, RuntimeStatus>;
    health: Record<RuntimeName, RuntimeHealth>;
  } {
    const status: Partial<Record<RuntimeName, RuntimeStatus>> = {};
    const health: Partial<Record<RuntimeName, RuntimeHealth>> = {};

    for (const [name, runtime] of this.registry.entries()) {
      status[name] = runtime.status;
      health[name] = runtime.getInfo().health;
    }

    const readyCount = Object.values(status).filter((s) => s === 'ready').length;

    return {
      initialized: this.state.initialized,
      runtimeCount: this.registry.size,
      readyCount,
      uptime: this.getUptime(),
      status: status as Record<RuntimeName, RuntimeStatus>,
      health: health as Record<RuntimeName, RuntimeHealth>,
    };
  }

  getLatencyStats(): Map<RuntimeName, { count: number; sum: number; avg: number; min: number; max: number }> {
    return this.dispatcher.getLatencyStats();
  }

  getUptime(): number {
    return Math.floor((Date.now() - this.state.startupTime) / 1000);
  }

  isInitialized(): boolean {
    return this.state.initialized;
  }

  // ============================================
  // 6. 统一等待策略 + 响应解包（P2 性能优化）
  // ============================================

  /**
   * 统一等待 RuntimeManager 初始化（或指定 Runtime 就绪）
   *
   * 目的：替代各 Workspace 中重复的轮询实现（GeoDsoCommandCenter/HermesMemoryPanel/
   * MarketingStudioWorkspace/ProjectsWorkspace/SOPLibrary/KnowledgeWorkspace 等）。
   *
   * @param options.timeoutMs 总超时（默认 30s）
   * @param options.intervalMs 轮询间隔（默认 200ms）
   * @param options.name 可选：指定 Runtime 名，等待其 status === 'ready'
   * @returns 是否就绪
   */
  static async waitForRuntime(options: {
    timeoutMs?: number;
    intervalMs?: number;
    name?: RuntimeName;
  } = {}): Promise<boolean> {
    const { timeoutMs = 30000, intervalMs = 200, name } = options;
    const deadline = Date.now() + timeoutMs;
    const manager = RuntimeManager.getInstance();

    // 等待初始化
    while (!manager.isInitialized()) {
      if (Date.now() > deadline) return false;
      await new Promise((r) => setTimeout(r, intervalMs));
    }

    // 等待指定 Runtime 就绪
    if (name) {
      while (true) {
        try {
          const runtime = manager.getRuntime(name);
          if (runtime && runtime.status === 'ready') return true;
        } catch {
          // runtime 未注册，视为不可用
        }
        if (Date.now() > deadline) return false;
        await new Promise((r) => setTimeout(r, intervalMs));
      }
    }
    return true;
  }

  /**
   * 递归解包 Runtime 响应，取出真实数据
   *
   * 背景：RuntimeManager.query 返回 { success, data }，而部分 Runtime（如
   * ContentStrategy / RevenueEngine / KnowledgeEvolution 等）的 data 内部还包了一层
   * { success, data }，真实数据可能在 resp.data.data。此函数递归剥离。
   *
   * @param resp RuntimeManager.query 的原始返回
   * @returns 真实数据（解包后），无数据时返回 null
   */
  static unwrapResponse<T = unknown>(resp: any): T | null {
    if (!resp) return null;
    let current = resp;
    let guard = 0;
    while (current && typeof current === 'object' && guard < 10) {
      if ('data' in current) {
        const next = (current as any).data;
        // 若下一层仍是 { success, data }，继续剥
        if (next && typeof next === 'object' && 'data' in next) {
          current = next;
          guard++;
          continue;
        }
        return next as T;
      }
      break;
    }
    return (current === undefined ? null : current) as T;
  }

  /**
   * 统一解包的查询：query + unwrapResponse 一步到位
   *
   * 示例：const data = await RuntimeManager.getInstance().queryUnwrapped<T>(RuntimeName.Finance, {...});
   */
  async queryUnwrapped<T = unknown>(
    name: RuntimeName,
    params?: Record<string, any>,
    workspaceId?: string,
  ): Promise<T | null> {
    const resp = await this.query(name, params, workspaceId);
    if (!resp.success) {
      return null;
    }
    return RuntimeManager.unwrapResponse<T>(resp);
  }


  // ============================================
  // 5b. 实例委托方法（静态方法的实例别名，方便调用方使用单例调用）
  // ============================================

  /** 实例委托：等待 Runtime 就绪 */
  async waitForRuntime(options: {
    timeoutMs?: number;
    intervalMs?: number;
    name?: RuntimeName;
  } = {}): Promise<boolean> {
    return RuntimeManager.waitForRuntime(options);
  }

  /** 实例委托：递归解包 Runtime 响应 */
  unwrapResponse<T = unknown>(resp: any): T | null {
    return RuntimeManager.unwrapResponse<T>(resp);
  }


  // ============================================
  // 6. Utility（仅用于调试）
  // ============================================
  getState(): RuntimeManagerState {
    return { ...this.state };
  }

  getEventBus(): EventBus {
    return this.eventBus;
  }

  getRegistry(): RuntimeRegistry {
    return this.registry;
  }

  getDispatcher(): RuntimeDispatcher {
    return this.dispatcher;
  }
}

// ============================================
// 导出单例
// ============================================
const runtimeManager = RuntimeManager.getInstance();
export default runtimeManager;